import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Grid,
  Tabs,
  Tab,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, Add as AddIcon, DragIndicator as DragIcon, Save as SaveIcon, Cancel as CancelIcon, CloudUpload as UploadIcon } from '@mui/icons-material';
import api from '../../utils/api';
import ItemEditor from './ItemEditor';
import CSVUploadDialog from './CSVUploadDialog';
import { CSVItem, normalizeItemType } from '../../utils/csvUtils';
import { buildChecklistItemWritePayload, nextRevisionLabel } from '../../utils/checklistItemUtils';

interface ChecklistInstance {
  id?: number;
  checklist: number;
  instance_name: string;
  instance_identifier: string;
  location_details: string;
  order: number;
  is_active: boolean;
}

interface Checklist {
  id: number;
  title: string;
  description: string;
  category: string;
  version: number;
  is_active: boolean;
  require_all_items: boolean;
  minimum_completion_percentage: number;
  total_items: number;
  required_items: number;
  created_at: string;
  updated_at: string;
  instances?: ChecklistInstance[];
}

interface ChecklistItem {
  id?: number;
  checklist?: number;
  parent_item?: number | null;
  title: string;
  description: string;
  item_type: string;
  order: number;
  is_required: boolean;
  validation_rules: any;
  options: string[];
  default_value: string;
  allowed_file_types: string;
  max_file_size_mb: number;
  is_visible: boolean;
  show_when_parent_completed: boolean;
}

interface ChecklistBuilderProps {
  open: boolean;
  onClose: () => void;
  checklist?: Checklist | null;
}

const categoryOptions = [
  { value: 'safety', label: 'Safety' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'quality', label: 'Quality Control' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'audit', label: 'Audit' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'training', label: 'Training' },
  { value: 'other', label: 'Other' }
];

const itemTypeOptions = [
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'text_input', label: 'Text Input' },
  { value: 'number_input', label: 'Number Input' },
  { value: 'file_upload', label: 'File Upload' },
  { value: 'date_input', label: 'Date Input' },
  { value: 'time_input', label: 'Time Input' },
  { value: 'select', label: 'Dropdown Selection' },
  { value: 'multi_select', label: 'Multi-select' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'signature', label: 'Digital Signature' },
  { value: 'location', label: 'Location/GPS' },
  { value: 'photo', label: 'Photo Capture' }
];

const ChecklistBuilder: React.FC<ChecklistBuilderProps> = ({
  open,
  onClose,
  checklist
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [editingItem, setEditingItem] = useState<ChecklistItem | null>(null);
  const [itemEditorOpen, setItemEditorOpen] = useState(false);
  const [csvUploadOpen, setCsvUploadOpen] = useState(false);
  
  // Instance state
  const [instances, setInstances] = useState<ChecklistInstance[]>([]);
  const [editingInstance, setEditingInstance] = useState<ChecklistInstance | null>(null);
  const [instanceDialogOpen, setInstanceDialogOpen] = useState(false);
  const [instanceCsvUploadOpen, setInstanceCsvUploadOpen] = useState(false);
  
  // Revision state
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState<any>(null);

  // Form state
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'other',
    require_all_items: true,
    minimum_completion_percentage: 100
  });

  // Fetch functions
  const fetchChecklistItems = async () => {
    if (!checklist) return;
    
    try {
      const response = await api.get(`/checklist-items/?checklist=${checklist.id}`);
      setItems(response.data.results || response.data);
    } catch (err) {
      console.error('Error fetching checklist items:', err);
    }
  };

  const fetchChecklistInstances = async () => {
    if (!checklist) return;
    
    try {
      const response = await api.get(`/checklist-instances/?checklist=${checklist.id}`);
      setInstances(response.data.results || response.data);
    } catch (err) {
      console.error('Error fetching checklist instances:', err);
    }
  };

  useEffect(() => {
    if (checklist) {
      setForm({
        title: checklist.title,
        description: checklist.description,
        category: checklist.category,
        require_all_items: checklist.require_all_items,
        minimum_completion_percentage: checklist.minimum_completion_percentage
      });
      fetchChecklistItems();
      fetchChecklistInstances();
    } else {
      setForm({
        title: '',
        description: '',
        category: 'other',
        require_all_items: true,
        minimum_completion_percentage: 100
      });
      setItems([]);
      setInstances([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checklist]);

  const handleFormChange = (field: string, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAddItem = () => {
    const newItem: ChecklistItem = {
      title: '',
      description: '',
      item_type: 'checkbox',
      order: items.length + 1,
      is_required: true,
      validation_rules: {},
      options: [],
      default_value: '',
      allowed_file_types: '',
      max_file_size_mb: 10,
      is_visible: true,
      show_when_parent_completed: false
    };
    setEditingItem(newItem);
    setItemEditorOpen(true);
  };

  const handleEditItem = (item: ChecklistItem) => {
    setEditingItem(item);
    setItemEditorOpen(true);
  };

  const handleDeleteItem = async (itemId: number) => {
    try {
      // Check if this is a temporary local item (has timestamp ID) or a real server item
      if (itemId > 1000000000000) { // Timestamp-based temporary ID
        // Remove from local state only
        setItems(prev => prev.filter(item => item.id !== itemId));
      } else {
        // Delete from server
        await api.delete(
          `/checklist-items/${itemId}/`
        );
        setItems(prev => prev.filter(item => item.id !== itemId));
      }
    } catch (err) {
      setError('Failed to delete item. Please try again.');
      console.error('Error deleting item:', err);
    }
  };

  const isTemporaryItemId = (id?: number) => id !== undefined && id > 1000000000000;

  const handleItemSave = async (item: ChecklistItem) => {
    try {
      const hasServerId = item.id !== undefined && !isTemporaryItemId(item.id);
      const itemPayload = buildChecklistItemWritePayload(item);

      if (hasServerId) {
        const response = await api.patch(
          `/checklist-items/${item.id}/`,
          itemPayload
        );
        setItems(prev => prev.map(i => i.id === item.id ? response.data : i));
      } else if (checklist?.id) {
        const itemData = { ...itemPayload, checklist: checklist.id };
        const response = await api.post('/checklist-items/', itemData);
        setItems(prev =>
          item.id
            ? prev.map(i => (i.id === item.id ? response.data : i))
            : [...prev, response.data]
        );
      } else if (item.id) {
        setItems(prev => prev.map(i => (i.id === item.id ? item : i)));
      } else {
        const newItem = { ...item, id: Date.now() };
        setItems(prev => [...prev, newItem]);
      }
      setItemEditorOpen(false);
      setEditingItem(null);
    } catch (err) {
      setError('Failed to save item. Please try again.');
      console.error('Error saving item:', err);
    }
  };

  // Instance management functions
  const handleAddInstance = () => {
    if (!checklist?.id) {
      setError('Please save the checklist first before adding instances.');
      return;
    }
    setEditingInstance({
      checklist: checklist.id,
      instance_name: '',
      instance_identifier: '',
      location_details: '',
      order: instances.length,
      is_active: true
    });
    setInstanceDialogOpen(true);
  };

  const handleEditInstance = (instance: ChecklistInstance) => {
    setEditingInstance(instance);
    setInstanceDialogOpen(true);
  };

  const handleDeleteInstance = async (instanceId: number) => {
    if (!window.confirm('Are you sure you want to delete this instance?')) return;
    
    try {
      await api.delete(`/checklist-instances/${instanceId}/`);
      setInstances(prev => prev.filter(inst => inst.id !== instanceId));
    } catch (err) {
      setError('Failed to delete instance. Please try again.');
      console.error('Error deleting instance:', err);
    }
  };

  const handleInstanceSave = async () => {
    if (!editingInstance) return;
    
    try {
      if (editingInstance.id) {
        // Update existing instance
        const response = await api.patch(`/checklist-instances/${editingInstance.id}/`, editingInstance);
        setInstances(prev => prev.map(inst => inst.id === editingInstance.id ? response.data : inst));
      } else {
        // Create new instance
        const response = await api.post('/checklist-instances/', editingInstance);
        setInstances(prev => [...prev, response.data]);
      }
      setInstanceDialogOpen(false);
      setEditingInstance(null);
    } catch (err) {
      setError('Failed to save instance. Please try again.');
      console.error('Error saving instance:', err);
    }
  };

  const handleInstanceCsvUpload = async (file: File) => {
    if (!checklist?.id) {
      setError('Please save the checklist first before importing instances.');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('checklist_id', checklist.id.toString());

      const response = await api.post('/checklist-instances/bulk_import/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setInstances(response.data.instances);
      setInstanceCsvUploadOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to import instances. Please check your CSV format.');
      console.error('Error importing instances:', err);
    }
  };

  const handleDownloadInstanceTemplate = async () => {
    try {
      const response = await api.get('/checklist-instances/download_template/', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'checklist_instances_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to download template.');
      console.error('Error downloading template:', err);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      if (checklist) {
        // Update existing checklist
        await api.patch(
          `/checklists/${checklist.id}/`, 
          form
        );
        
        // Persist all items (new items and edits made without using Save Item)
        for (const item of items) {
          const itemPayload = buildChecklistItemWritePayload(item);
          if (item.id && !isTemporaryItemId(item.id)) {
            await api.patch(`/checklist-items/${item.id}/`, itemPayload);
          } else {
            await api.post('/checklist-items/', {
              ...itemPayload,
              checklist: checklist.id,
            });
          }
        }
      } else {
        // Create new checklist with items
        const checklistData = { 
          ...form, 
          items: items.map((item) => buildChecklistItemWritePayload(item))
        };
        await api.post(
          '/checklists/', 
          checklistData
        );
      }

      onClose();
    } catch (err: any) {
      // Log the full error response for debugging
      console.error('Error saving checklist:', err);
      console.error('Error response data:', err.response?.data);
      
      // Check if this is a revision requirement error
      if (err.response?.data?.requires_revision === true) {
        // Store the pending changes and show revision dialog
        setPendingSaveData({ form, items, instances });
        setRevisionDialogOpen(true);
        setLoading(false);
        return;
      }
      
      // Try to extract a meaningful error message
      let errorMessage = 'Failed to save checklist. Please try again.';
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.detail) {
          errorMessage = err.response.data.detail;
        } else {
          // Try to format field-specific errors
          const errors = Object.entries(err.response.data)
            .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
            .join('; ');
          if (errors) errorMessage = errors;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRevision = async () => {
    if (!checklist) return;
    
    try {
      setLoading(true);
      setError(null);
      setRevisionDialogOpen(false);
      
      const pending = pendingSaveData;
      await api.post(`/checklists/${checklist.id}/create_revision/`, pending ? {
        ...pending.form,
        items: (pending.items || []).map((item: ChecklistItem) => buildChecklistItemWritePayload(item)),
      } : {});
      
      setPendingSaveData(null);
      onClose();
    } catch (err: any) {
      console.error('Error creating revision:', err);
      setError('Failed to create revision. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRevision = () => {
    setRevisionDialogOpen(false);
    setPendingSaveData(null);
  };

  const handleCancel = () => {
    setForm({
      title: '',
      description: '',
      category: 'other',
      require_all_items: true,
      minimum_completion_percentage: 100
    });
    setItems([]);
    setError(null);
    onClose();
  };

  const getItemTypeLabel = (type: string) => {
    return itemTypeOptions.find(option => option.value === type)?.label || type;
  };

  const handleCSVImport = (csvItems: CSVItem[], replaceExisting: boolean) => {
    try {
      const baseTempId = Date.now();
      const convertedItems: ChecklistItem[] = csvItems.map((csvItem, index) => ({
        id: baseTempId + index,
        title: csvItem.title,
        description: csvItem.description,
        item_type: normalizeItemType(csvItem.item_type),
        order: csvItem.order,
        is_required: csvItem.is_required,
        validation_rules: {},
        options: csvItem.options ?? [],
        default_value: '',
        allowed_file_types: '',
        max_file_size_mb: 10,
        is_visible: true,
        show_when_parent_completed: false
      }));

      if (replaceExisting) {
        setItems(convertedItems);
      } else {
        const maxOrder = items.length > 0 ? Math.max(...items.map(item => item.order)) : 0;
        const adjustedItems = convertedItems.map((item, index) => ({
          ...item,
          id: baseTempId + index,
          order: maxOrder + index + 1
        }));
        setItems(prev => [...prev, ...adjustedItems]);
      }

      setError(null);
    } catch (err) {
      setError('Failed to import CSV items. Please check the file format.');
      console.error('Error importing CSV:', err);
    }
  };

  const renderBasicInfoTab = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Basic Information
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Checklist Title"
            value={form.title}
            onChange={(e) => handleFormChange('title', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            value={form.description}
            onChange={(e) => handleFormChange('description', e.target.value)}
            multiline
            rows={3}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={form.category}
              label="Category"
              onChange={(e) => handleFormChange('category', e.target.value)}
            >
              {categoryOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={form.require_all_items}
                onChange={(e) => handleFormChange('require_all_items', e.target.checked)}
              />
            }
            label="Require all items to be completed"
          />
        </Grid>
        {!form.require_all_items && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Minimum completion percentage"
              value={form.minimum_completion_percentage}
              onChange={(e) => handleFormChange('minimum_completion_percentage', parseInt(e.target.value))}
              inputProps={{ min: 1, max: 100 }}
              helperText="Percentage of items that must be completed (1-100)"
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );

  const renderItemsTab = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Checklist Items ({items.length})
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setCsvUploadOpen(true)}
          >
            Import CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddItem}
          >
            Add Item
          </Button>
        </Box>
      </Box>

      {items.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No items added yet
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Add items to your checklist to get started
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddItem}
          >
            Add Your First Item
          </Button>
        </Box>
      ) : (
        <Box>
          {items.map((item, index) => (
            <Box
              key={item.id || index}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}
            >
              <IconButton aria-label="drag">
                <DragIcon color="action" />
              </IconButton>
              <Box flex={1}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {item.title || 'Untitled Item'}
                  </Typography>
                  <Chip
                    label={getItemTypeLabel(item.item_type)}
                    size="small"
                    variant="outlined"
                  />
                  {item.is_required && (
                    <Chip
                      label="Required"
                      size="small"
                      color="error"
                    />
                  )}
                </Box>
                {item.description && (
                  <Typography variant="body2" color="text.secondary">
                    {item.description}
                  </Typography>
                )}
              </Box>
              <Box display="flex" gap={1}>
                <Button
                  size="small"
                  onClick={() => handleEditItem(item)}
                >
                  Edit
                </Button>
                {item.id && (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleDeleteItem(item.id!)}
                  >
                    Delete
                  </Button>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );

  const renderInstancesTab = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Checklist Instances ({instances.length})
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<UploadIcon />}
            onClick={handleDownloadInstanceTemplate}
          >
            Download Template
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<UploadIcon />}
            onClick={() => setInstanceCsvUploadOpen(true)}
            disabled={!checklist?.id}
          >
            Import CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddInstance}
            disabled={!checklist?.id}
          >
            Add Instance
          </Button>
        </Box>
      </Box>

      {!checklist?.id && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Please save the checklist first before adding instances.
        </Alert>
      )}

      {instances.length === 0 ? (
        <Alert severity="info">
          No instances defined. This checklist will use a single default instance.
          <br />
          <Typography variant="body2" sx={{ mt: 1 }}>
            Use instances when you need to apply the same checklist to multiple items 
            (e.g., 50 fire extinguishers that need monthly inspection).
          </Typography>
        </Alert>
      ) : (
        <List>
          {instances.map((instance, index) => (
            <ListItem
              key={instance.id || index}
              secondaryAction={
                <Box>
                  <IconButton edge="end" onClick={() => handleEditInstance(instance)} sx={{ mr: 1 }}>
                    <EditIcon />
                  </IconButton>
                  <IconButton edge="end" onClick={() => instance.id && handleDeleteInstance(instance.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              }
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DragIcon sx={{ color: 'text.secondary' }} />
                    <Typography variant="body1" fontWeight="medium">
                      {instance.instance_identifier || `Instance #${index + 1}`}: {instance.instance_name}
                    </Typography>
                  </Box>
                }
                secondary={
                  instance.location_details && (
                    <Typography variant="body2" color="text.secondary">
                      {instance.location_details}
                    </Typography>
                  )
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );

  const renderPreviewTab = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Preview
      </Typography>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 3,
          bgcolor: 'background.paper'
        }}
      >
        <Typography variant="h5" gutterBottom>
          {form.title || 'Untitled Checklist'}
        </Typography>
        {form.description && (
          <Typography variant="body1" color="text.secondary" mb={3}>
            {form.description}
          </Typography>
        )}
        
        <Box display="flex" gap={1} mb={3}>
          <Chip
            label={categoryOptions.find(c => c.value === form.category)?.label || form.category}
            color="primary"
            size="small"
          />
        </Box>

        <Typography variant="h6" gutterBottom>
          Items ({items.length})
        </Typography>
        
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No items added yet
          </Typography>
        ) : (
          <Box>
            {items.map((item, index) => (
              <Box
                key={index}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  mb: 2
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {item.title || 'Untitled Item'}
                  </Typography>
                  <Chip
                    label={getItemTypeLabel(item.item_type)}
                    size="small"
                    variant="outlined"
                  />
                  {item.is_required && (
                    <Chip
                      label="Required"
                      size="small"
                      color="error"
                    />
                  )}
                </Box>
                {item.description && (
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {item.description}
                  </Typography>
                )}
                <Box
                  sx={{
                    bgcolor: 'grey.100',
                    p: 1,
                    borderRadius: 1,
                    minHeight: 40,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {getItemTypeLabel(item.item_type)} input will appear here
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle>
          {checklist ? 'Edit Checklist' : 'Create New Checklist'}
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
              <Tab label="Basic Information" />
              <Tab label="Items" />
              <Tab label="Instances" />
              <Tab label="Preview" />
            </Tabs>
          </Box>

          {activeTab === 0 && renderBasicInfoTab()}
          {activeTab === 1 && renderItemsTab()}
          {activeTab === 2 && renderInstancesTab()}
          {activeTab === 3 && renderPreviewTab()}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancel} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
            disabled={loading || !form.title.trim()}
          >
            {loading ? 'Saving...' : 'Save Checklist'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Item Editor Modal */}
      <ItemEditor
        open={itemEditorOpen}
        onClose={() => {
          setItemEditorOpen(false);
          setEditingItem(null);
        }}
        item={editingItem}
        onSave={handleItemSave}
        itemTypeOptions={itemTypeOptions}
      />

      {/* CSV Upload Modal */}
      <CSVUploadDialog
        open={csvUploadOpen}
        onClose={() => setCsvUploadOpen(false)}
        onImport={handleCSVImport}
        existingItemCount={items.length}
      />

      {/* Instance Edit Dialog */}
      <Dialog open={instanceDialogOpen} onClose={() => setInstanceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingInstance?.id ? 'Edit Instance' : 'Add Instance'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Instance Name"
              value={editingInstance?.instance_name || ''}
              onChange={(e) => setEditingInstance(prev => prev ? { ...prev, instance_name: e.target.value } : null)}
              fullWidth
              required
              helperText="E.g., 'Building A - 1st Floor - East'"
            />
            <TextField
              label="Instance Identifier"
              value={editingInstance?.instance_identifier || ''}
              onChange={(e) => setEditingInstance(prev => prev ? { ...prev, instance_identifier: e.target.value } : null)}
              fullWidth
              helperText="Optional unique code (e.g., 'EXT-001')"
            />
            <TextField
              label="Location Details"
              value={editingInstance?.location_details || ''}
              onChange={(e) => setEditingInstance(prev => prev ? { ...prev, location_details: e.target.value } : null)}
              fullWidth
              multiline
              rows={3}
              helperText="Additional location information"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInstanceDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleInstanceSave} 
            variant="contained"
            disabled={!editingInstance?.instance_name.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Instance CSV Upload Dialog */}
      <Dialog open={instanceCsvUploadOpen} onClose={() => setInstanceCsvUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Instances from CSV</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            Upload a CSV file with columns: instance_name (required), instance_identifier, location_details, order
          </Alert>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            startIcon={<UploadIcon />}
          >
            Select CSV File
            <input
              type="file"
              hidden
              accept=".csv"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleInstanceCsvUpload(file);
                }
              }}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInstanceCsvUploadOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Revision Confirmation Dialog */}
      <Dialog 
        open={revisionDialogOpen} 
        onClose={handleCancelRevision}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Checklist Revision?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This checklist has been started by users and cannot be edited directly.
          </Alert>
          <Typography variant="body1" paragraph>
            Your changes will be saved as a new revision (version {checklist ? nextRevisionLabel(checklist.version) : ''}).
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • The current version will remain active for in-progress assignments<br />
            • New assignments will use the new revision<br />
            • All your changes will be preserved in the new version
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRevision} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreateRevision} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? 'Creating Revision...' : 'Create Revision'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChecklistBuilder; 